import React, { useCallback, useState } from 'react';
import { isEmptyField, isInvalidEmail } from '../../../../utils';
import { useDistribution, useToast } from '../../../../hooks';
import { updateRecipient } from '../../../../services';
import { CreateRecipient, Recipient, UpdateRecipient } from '../../../../dtos';
import { Button, ConfirmModal, Input, InputPhone, Modal, ModalProps } from '../../../../components';
import { PlusMinusAmount } from '../index';
import { Container } from './styles';

type Props = ModalProps & {
    recipientData?: {
        recipient: CreateRecipient | UpdateRecipient;
        index: number;
    };
    modalType?: string;
    handleSave?: (recipient: Recipient) => void;
}

type FormError = {
    field: string;
    message: string;
}

type InputError = {
    [field: string]: boolean;
}

type FormData = {
    email?: string;
    name?: string;
    phoneNumber?: string;
    amount?: number;
}

const RecipientModal: React.FC<Props> = ({ recipientData, handleClose, handleSave, modalType, ...rest }) => {
    const [data, setData] = useState<FormData>({
        ...recipientData?.recipient
    });
    const [errors, setErrors] = useState<FormError[]>([]);
    const [inputErrors, setInputErrors] = useState<InputError>({});
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState<boolean>(false);

    const { addRecipient, editRecipient, removeRecipient, recipients, availableAmount } = useDistribution();
    const { showToast } = useToast();

    const handleChange = useCallback((newData: FormData) => setData(oldData => ({
        ...oldData,
        ...newData
    })), []);

    const isUpdate = modalType === 'update';

    const saveRecipient = useCallback(() => {
        const { email, name, phoneNumber, amount = 1 } = data;

        if (!email || !name)
            throw new Error('The states email and name cannot be null');

        if(isUpdate) {
            const update : UpdateRecipient = {
                ...recipientData?.recipient as UpdateRecipient,
                ...data,
            }
            updateRecipient(update).then(recipient => {
                showToast({ type: 'success', message: 'Número de celular adicionado' });
                handleSave && handleSave({...data, ...recipient as Recipient});
                handleClose();
            })
            return;
        }

        if (
            recipientData?.recipient.email !== email && recipients.map(({ email }) => email).includes(email)
        ) {
            setErrors(oldErrors => [...oldErrors, {
                field: 'email',
                message: 'Destinatário já presente na distribuição. Utilize outro.'
            }]);
            return;
        }

        if (recipientData) {
            editRecipient(recipientData.index, {
                email,
                name,
                phoneNumber,
                amount
            });
        } else {
            addRecipient({
                email,
                name,
                phoneNumber,
                amount: 1
            });
        }

        handleClose();
    }, [data, isUpdate, recipientData, recipients, handleClose, showToast, handleSave, editRecipient, addRecipient]);

    const isAddButtonDisabled = useCallback(() => {
        const { email, name } = data;

        return isEmptyField(email) || isInvalidEmail(email) || isEmptyField(name) || Object.values(inputErrors).filter(error => error).length > 0;
    }, [data, inputErrors]);

    return (
        <Modal
            id="recipientmodal"
            handleClose={handleClose}
            {...rest}
        >
            {showConfirmDeleteModal && recipientData && (
                <ConfirmModal
                    title="Excluir destinatário"
                    content="Deseja realmente excluir este destinatário?"
                    cancelButtonLabel="Não"
                    confirmButtonLabel="Sim, excluir"
                    handleClose={() => setShowConfirmDeleteModal(false)}
                    handleConfirmCancel={() => {
                        removeRecipient(recipientData.index);
                        setShowConfirmDeleteModal(false);
                        handleClose();
                    }}
                />
            )}
            <Container >
                <span >(*) Preenchimento obrigatório</span>
                <form >
                    <Input
                        disabled={isUpdate}
                        id="recipientmodal.email"
                        type="email"
                        required
                        label="E-mail"
                        placeholder="Digite o e-mail do destinatário"
                        value={data.email || ''}
                        onChange={(e) => handleChange({
                            email: e.target.value
                        })}
                        error={errors.filter(({ field }) => field === 'email')[0]?.message}
                    />
                    <Input
                        disabled={isUpdate}
                        id="recipientmodal.name"
                        type="text"
                        required
                        label="Nome"
                        placeholder="Digite o nome do destinatário"
                        value={data.name || ''}
                        maxLength={isUpdate ? undefined : 20}
                        onChange={(e) => handleChange({
                            name: e.target.value
                        })}
                        error={errors.filter(({ field }) => field === 'name')[0]?.message}
                        hasError={error => setInputErrors(oldInputErrors => ({
                            ...oldInputErrors,
                            name: error
                        }))}
                    />
                    <InputPhone
                        id="recipientmodal.phone"
                        label="Celular"
                        placeholder="Digite um celular de contato do destinatário"
                        defaultValue={recipientData?.recipient?.phoneNumber}
                        getValue={({ value }) => handleChange({
                            phoneNumber: value
                        })}
                    />
                </form>
                {(recipientData && !isUpdate) && (
                    <PlusMinusAmount
                        id="recipientmodal"
                        handleMinus={() => {
                            const { amount = 0 } = data;

                            if (amount > 1)
                                handleChange({
                                    amount: amount - 1
                                })
                        }}
                        handlePlus={() => {
                            const { amount = 0 } = data;

                            if (amount < (availableAmount + recipientData.recipient.amount))
                                handleChange({
                                    amount: amount + 1
                                })
                        }}
                        amount={data.amount || 0}
                    />
                )}
                <Button
                    id="recipientmodal.savebutton"
                    disabled={isAddButtonDisabled()}
                    onClick={saveRecipient}
                >
                    {recipientData ? 'Salvar' : 'Adicionar'}
                </Button>
                {(recipientData && !isUpdate) && (
                    <Button
                        id="recipientmodal.deletebutton"
                        label
                        onClick={() => setShowConfirmDeleteModal(true)}
                    >
                        Excluir
                    </Button>
                )}
            </Container>
        </Modal>
    );
};

export { RecipientModal };
